// White-label prequalification — the partner "skin" DTO + fetcher.
//
// A partner brand is resolved by slug from GET /prequalification/brand/{slug}
// (public, anonymous — same portal client, same /api/portal isolation). The
// skin drives the runtime theme (colors / fonts / wordmark / contact / hero
// copy) applied by PartnerThemeProvider. An unknown or inactive slug 404s,
// which fetchPartnerBrand surfaces as `null` (the funnel falls back to the
// native Phoenix experience).
//
// A buyer TEAM's site (`usesPhoenixSkin: true`) is NOT a 404: it answers 200
// with a minimal skin — the slug and `usesPhoenixSkin`, none of the partner
// presentation. The funnel renders it exactly as the native funnel (no theme
// wrapper, no partner chrome, no "couldn't find that partner" notice) while
// the slug stays resolved, so the finalize can credit the team.

import { portalApi } from "./client";
import { PortalApiError } from "./client";

export type PartnerLogoMode = "WordmarkText" | "Asset";

export interface PartnerBrandColors {
  primary: string;
  foreground: string;
  pressed: string;
  background: string;
  surface: string;
  accent: string;
  bodyText: string;
  hairline: string;
}

export interface PartnerBrandSkin {
  slug: string;
  displayName: string;
  referenceCode: string;
  logoMode: PartnerLogoMode;
  wordmarkText: string;
  logoAssetPath: string | null;
  /** Time-limited read SAS URL for the uploaded logo (Asset mode), resolved
   *  server-side. Null in wordmark mode / when unresolved — render the
   *  wordmark text instead. */
  logoUrl: string | null;
  colors: PartnerBrandColors;
  fontDisplay: string;
  fontBody: string;
  heroHeadline: string | null;
  heroSubcopy: string | null;
  contactCity: string;
  contactPhone: string;
  contactWebsite: string;
  contactEmail: string | null;
  /** D2a — a buyer team's site. Render as the NATIVE funnel (no partner theme,
   *  no partner chrome, no fallback notice) but keep the slug for attribution.
   *  Optional so a fixture / older response without it reads as a partner. */
  usesPhoenixSkin?: boolean;
}

/** Resolve a partner brand by slug. Returns `null` on a 404 (unknown /
 *  inactive partner); other (transient) errors reject so the caller can tell
 *  "not found" from "couldn't load" and avoid a misleading fallback notice. */
export async function fetchPartnerBrand(slug: string): Promise<PartnerBrandSkin | null> {
  try {
    return await portalApi.get<PartnerBrandSkin>(`/prequalification/brand/${encodeURIComponent(slug)}`);
  } catch (err) {
    if (err instanceof PortalApiError && err.status === 404) return null;
    throw err;
  }
}
